import React, { useEffect, useState } from "react";
import FullScreenModal from "@/features/shell/components/FullScreenModal/FullScreenModal";
import { stringify } from "query-string";
import {
  ScrollArea,
  Typography,
  Button,
  NativeSelect,
  Form,
  toast,
  SkeletonParagraph,
} from "@picklebet/ui";

import * as styles from "./MigrateToIOMLicenceModal.module.scss";
import cx from "classnames";
import { getAllCountries } from "@/utilities/address";
import { Controller, useForm } from "react-hook-form";

import { useProfile } from "@/hooks/firestore/user/useProfile";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { message } from "@/utilities/message/message";
import { useHasPermission } from "@/features/auth/hooks/usePermission";
import { convertErrorToToast } from "@/utilities/errorsParser";
import * as Session from "@/features/auth/services/session";
import * as support from "@/utilities/support";
import {
  CustomerLicenceOptions,
  useAuthStore,
} from "@/features/auth/stores/authStore";
import { useShellStore } from "@/features/shell/stores/shellStore";
import { telemetry } from "@/utilities/telemetry";

const doArticleSearch = async (labels: string[]) => {
  const zendeskSearchUrl = `https://${ZENDESK_DOMAIN}/api/v2/help_center/articles/embeddable_search.json`;
  const query = {
    locale: "en-us",
    per_page: "20",
    origin: "web_widget",
    label_names: labels,
  };
  try {
    const response = await fetch(
      `${zendeskSearchUrl}?${stringify(query, { arrayFormat: "comma" })}`,
      {
        method: "GET",
      },
    );
    return await response.json();
  } catch (error) {
    toast(
      convertErrorToToast(error, {
        title: "Something went wrong",
      }),
    );
  }
};

const nationalityOptions = getAllCountries();

const schema = z.object({
  nationality: z.string().min(2, "Please choose a nationality"),
});

export type FormData = z.infer<typeof schema>;

const MODAL_IDENTIFIER = "migrateToIOMLicence";
// IOM zendesk domains, use sandbox in staging - hardcoded as this is specific to IOM and shouldn't run for very long
const ZENDESK_DOMAIN =
  process.env.GATSBY_SKRILLA_ENV === "production"
    ? `picklebet-international.zendesk.com`
    : `picklebet-international1745290020.zendesk.com`;

const MigrateToIOMLicenceModal = () => {
  const modal = useShellStore((state) => state.modal);
  const setModal = useShellStore((state) => state.setModal);
  const setZendeskTemp = useAuthStore((state) => state.setZendeskTemp);
  const [response, setResponse] = useState<{
    results: { body: string }[];
  } | null>(null);
  const { profile, isLoading: isLoadingProfile } = useProfile();

  // use the store to correctly release the body scroll on close
  const isOpen = modal === MODAL_IDENTIFIER;

  const nationality = profile?.nationality || profile?.country || "";
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const shouldMigrateToIOMLicence = useHasPermission("migratedAccount", false);

  const {
    control,
    formState: { errors },
    setValue,
    handleSubmit,
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      nationality,
    },
  });
  const nationalityFormValue = watch("nationality");

  useEffect(() => {
    // if it's not open, and it should be - open it
    if (!isOpen && shouldMigrateToIOMLicence) {
      setModal(MODAL_IDENTIFIER);
      setZendeskTemp({ zendeskDomain: ZENDESK_DOMAIN, zendeskKey: "" });
      return;
    }
    // if it's open, and it shouldn't be - close it
    if (isOpen && !shouldMigrateToIOMLicence) {
      setModal(null);

      // reset zendesk domains too
      setZendeskTemp({ zendeskDomain: "", zendeskKey: "" });
    }
  }, [shouldMigrateToIOMLicence, isOpen]);

  useEffect(() => {
    if (nationality !== nationalityFormValue) {
      setValue("nationality", nationality || "");
    }
  }, [nationality]);

  const onSubmit = async (data: FormData) => {
    const formData = {
      accepted: true,
      ...data,
      licence: CustomerLicenceOptions.ISLE_OF_MAN,
    };

    const [_response, error] = await message("accept-licence-terms", formData);
    if (error) {
      toast(
        convertErrorToToast(error, {
          title: "Something went wrong",
        }),
      );
      return;
    }
    telemetry.acceptedLicenceTerms({
      licence: CustomerLicenceOptions.ISLE_OF_MAN,
      accepted: true,
    });

    await handleClose(false);

    // reload so we get updated permissions
    window.location.reload();
  };

  const hasFormErrors = (Object.keys(errors)?.length || 0) >= 1;

  useEffect(() => {
    setIsLoadingContent(true);
    if (!shouldMigrateToIOMLicence) {
      setIsLoadingContent(false);
      return;
    }
    // guard against setting if component is unmounted
    let isMounted = true;

    (async () => {
      const data = await doArticleSearch(["weblink-terms-and-conditions"]);

      if (!isMounted) {
        return;
      }
      setResponse(data);
      setIsLoadingContent(false);
    })();

    return () => {
      isMounted = false;
    };
  }, [shouldMigrateToIOMLicence]);

  const content =
    response?.results?.[0]?.body ||
    "An error occurred while getting the terms, please contact our support team.";
  const handleClose = async (logout = true) => {
    if (logout) {
      await Session.signOut();
    }
    setModal(null);
  };

  const handleSupportLink = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();
    support.openWithChat();
  };

  const isLoading = isLoadingProfile || isLoadingContent;

  return (
    <FullScreenModal
      onClose={handleClose}
      isOpen={isOpen}
      className={styles.modal}
      contentClassName={styles.modalContent}
    >
      <Typography variant={"h1"}>Updated Terms</Typography>
      <div className={styles.infoText}>
        <Typography variant="body" className={styles.body}>
          By accepting these updated terms, you'll be eligible to access
          exciting new features, including our Online Casino offering! Stay
          tuned as we also plan to release expanded payment options later this
          year.
        </Typography>
        <Typography variant="body" className={styles.body}>
          You will continue to be able to withdraw and your existing balance and
          any pending bets will remain unchanged. You will also unlock the
          ability to deposit.
        </Typography>
        <Typography variant="body" className={styles.body}>
          If you decide against accepting these terms, please{" "}
          <a href="#" onClick={handleSupportLink}>
            contact our support team
          </a>{" "}
          to arrange a withdrawal of your balance (if applicable) and close your
          account.
        </Typography>
      </div>

      <Typography component="label" className={styles.label} noSpacing>
        Terms and Conditions
      </Typography>
      <ScrollArea className={cx(styles.scrollArea)} orientation="vertical">
        <Typography variant={`body`} className={styles.body}>
          {!isLoading && content ? (
            <div dangerouslySetInnerHTML={{ __html: content }} />
          ) : (
            <SkeletonParagraph />
          )}
        </Typography>
      </ScrollArea>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          control={control}
          name="nationality"
          render={({ field }) => (
            <NativeSelect
              {...field}
              options={nationalityOptions}
              label={"Nationality"}
              errorMessage={errors?.nationality?.message}
              placeholder={"Select your nationality"}
              loading={isLoading}
            />
          )}
        />
        <div className={styles.actions}>
          <div>
            <Button variant={"secondary"} onClick={() => handleClose()}>
              Cancel
            </Button>
          </div>
          <div className={cx(styles.flexFill, styles.flexEnd)}>
            <Button
              type={`submit`}
              disabled={hasFormErrors}
              loading={isLoading}
              className={cx(styles.flexFill, styles.submitButton)}
            >
              Accept Terms
            </Button>
          </div>
        </div>
      </Form>
    </FullScreenModal>
  );
};

export { MigrateToIOMLicenceModal };
